import React, { useEffect, useState, useCallback } from 'react';
import { X, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { IconBtn } from '@/components/editor-chrome/ui';

export type MarginSuggestionAccent = 'coral' | 'teal' | 'depth' | 'warning' | 'destructive';

export interface MarginSuggestion {
  id: string;
  /** CSS selector resolved against docRef.current — anchors the card vertically to that element. */
  anchorSel: string;
  title: string;
  detail: string;
  /** Free-form category badge (e.g., "topic", "block", "outline"). Rendered as the kicker text. */
  kind: string;
  /** Visual accent. Defaults to 'coral'. */
  accent?: MarginSuggestionAccent;
}

export interface MarginSuggestionsProps {
  /** The doc surface element. Anchor selectors are queried inside it; positions are relative to its top. */
  docRef: React.RefObject<HTMLElement | null>;
  suggestions: MarginSuggestion[];
  onApply: (id: string) => void;
  onDismiss: (id: string) => void;
  /** Width of each margin card in px. Default 230 (matches design). */
  cardWidth?: number;
  /** Pixel offset from the right edge of the doc. Default -20 (cards float in the gutter). */
  rightOffset?: number;
  className?: string;
}

const ACCENT_CLASSES: Record<MarginSuggestionAccent, { border: string; kicker: string; apply: string }> = {
  coral: {
    border: 'border-l-coral',
    kicker: 'text-coral',
    apply: 'bg-coral hover:bg-coral-hover text-white',
  },
  teal: {
    border: 'border-l-teal',
    kicker: 'text-teal-dark',
    apply: 'bg-teal hover:bg-teal-hover text-white',
  },
  depth: {
    border: 'border-l-[hsl(var(--semantic-depth))]',
    kicker: 'text-[hsl(var(--semantic-depth))]',
    apply: 'bg-coral hover:bg-coral-hover text-white',
  },
  warning: {
    border: 'border-l-[hsl(var(--status-warning))]',
    kicker: 'text-[hsl(var(--status-warning))]',
    apply: 'bg-coral hover:bg-coral-hover text-white',
  },
  destructive: {
    border: 'border-l-destructive',
    kicker: 'text-destructive',
    apply: 'bg-coral hover:bg-coral-hover text-white',
  },
};

/**
 * Frase-style margin AI suggestion cards.
 *
 * Cards are absolutely positioned in the doc gutter and vertically aligned
 * with their anchor element (resolved by `anchorSel` inside `docRef`). They
 * reposition on resize + scroll of the nearest [data-doc-scroll] container.
 *
 * Mount this inside a positioned ancestor of the doc (the design uses the
 * doc's own padded wrapper at editor-app.jsx:99). Cards have pointer-events
 * disabled on the wrapper so the doc text behind the gutter remains
 * clickable; the cards themselves re-enable pointer events.
 */
export function MarginSuggestions({
  docRef,
  suggestions,
  onApply,
  onDismiss,
  cardWidth = 230,
  rightOffset = -20,
  className,
}: MarginSuggestionsProps) {
  const [positions, setPositions] = useState<Record<string, { top: number }>>({});

  const compute = useCallback(() => {
    const el = docRef.current;
    if (!el) return;
    const docRect = el.getBoundingClientRect();
    const next: Record<string, { top: number }> = {};
    suggestions.forEach((s) => {
      const target = el.querySelector<HTMLElement>(s.anchorSel);
      if (target) {
        const r = target.getBoundingClientRect();
        next[s.id] = { top: r.top - docRect.top };
      }
    });
    setPositions(next);
  }, [docRef, suggestions]);

  useEffect(() => {
    compute();
    // The doc might still be measuring on first paint (web fonts, images). One
    // delayed recompute catches the late layout shift.
    const t = window.setTimeout(compute, 200);
    window.addEventListener('resize', compute);
    const scrollContainer = docRef.current?.closest('[data-doc-scroll]');
    scrollContainer?.addEventListener('scroll', compute);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('resize', compute);
      scrollContainer?.removeEventListener('scroll', compute);
    };
  }, [compute, docRef]);

  if (!suggestions.length) return null;

  return (
    <div
      className={cn('pointer-events-none absolute top-0', className)}
      style={{ right: rightOffset, width: cardWidth + 10 }}
      aria-label="Margin AI suggestions"
    >
      {suggestions.map((s) => {
        const pos = positions[s.id];
        if (!pos) return null;
        const accent = ACCENT_CLASSES[s.accent ?? 'coral'];
        return (
          <div
            key={s.id}
            className={cn(
              'pointer-events-auto absolute right-0',
              'rounded-md border border-border border-l-[3px] bg-card px-2.5 py-2 shadow-sm',
              'transition-[top] duration-200',
              accent.border,
            )}
            style={{ top: pos.top - 4, width: cardWidth }}
          >
            <div className="mb-1 flex items-center gap-1.5">
              <span
                className={cn(
                  'text-[9px] font-bold uppercase tracking-[0.05em]',
                  accent.kicker,
                )}
              >
                AI Suggestion · {s.kind}
              </span>
              <div className="flex-1" />
              <IconBtn
                icon={<X className="h-2.5 w-2.5" aria-hidden="true" />}
                label="Dismiss suggestion"
                size={16}
                onClick={() => onDismiss(s.id)}
              />
            </div>

            <div className="mb-1 text-[11.5px] font-semibold leading-[1.35] text-foreground">
              {s.title}
            </div>
            <div className="mb-2 text-[10.5px] leading-[1.4] text-muted-foreground">
              {s.detail}
            </div>

            <div className="flex gap-1">
              <button
                type="button"
                onClick={() => onApply(s.id)}
                className={cn(
                  'flex-1 inline-flex items-center justify-center gap-1',
                  'rounded px-1.5 py-0.5 text-[10.5px] font-semibold',
                  'border-0 transition-colors',
                  accent.apply,
                )}
              >
                <Plus className="h-2.5 w-2.5" aria-hidden="true" />
                Apply
              </button>
              <button
                type="button"
                onClick={() => onDismiss(s.id)}
                className={cn(
                  'rounded px-1.5 py-0.5 text-[10.5px] font-medium',
                  'border border-border bg-transparent text-muted-foreground',
                  'hover:bg-muted transition-colors',
                )}
              >
                Skip
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default MarginSuggestions;
